import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { observer } from "mobx-react";
import BankIcon from "../../../../../images/payment/bank-ach-hut.svg";
import VisaIcon from "../../../../../images/payment/visa-card_logo.svg";
import MasterCardIcon from "../../../../../images/payment/master-card-with-background.svg";
import DiscoverIcon from "../../../../../images/payment/discover-icon.svg";
import AmexIcon from "../../../../../images/payment/amex_card_logo.svg";
import GPayIcon from "../../../../../images/payment/gpay-icon.svg";
import ApplePayIcon from "../../../../../images/payment/apple-pay-icon.svg";
import badgeIcon from "../../../../../images/checkout/badge-blue.svg";
import PurchasePaymentSummarySection from "./PurchasePaymentSummarySection";
import PurchasePromotionSection from "./PurchasePromotionSection";
import PurchasePaymentFormInputs from "./PurchasePaymentFormInputs";
import PurchasePaymentItemSection from "./PurchasePaymentItemSection";
import {
  AddressModel,
  apiResponse,
  CheckoutPaymentPageModel,
  CountryWithStateModel,
  CustomerFacingCheckOutItemModel,
  MerchantAccountWithPaySettingsModel,
  PurchaseCheckOutAddOnDetails,
  PurchasePaymentIntentModel,
  PurchasePaymentModel,
  SellerModel,
} from "../models";
import { ICheckOutCombinedColors } from "../interfaces";
import {
  CustomLabel,
  FlexContainer,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { CheckOutPayFields, PaymentMethodTypes } from "../enums";
import { CheckoutItemsWrapper, MultiplePriceWrapper } from "../styles";
import { PromotionModel } from "../models/PromotionModels";
import NMIPaymentMethodForm, {
  NMIPaymentMethodFormHandle,
} from "./NMIPaymentMethodForm";

type IProps = {
  items: CustomerFacingCheckOutItemModel[];
  updateItems: (items: CustomerFacingCheckOutItemModel[]) => void;
  updateAddOns: (addOns: PurchaseCheckOutAddOnDetails[]) => void;
  payDetails: CheckoutPaymentPageModel;
  paymentSectionData: PurchasePaymentModel;
  updatePaymentSectionData: (data: PurchasePaymentModel) => void;
  countriesWithState: CountryWithStateModel[];
  addOns: PurchaseCheckOutAddOnDetails[];
  paymentIntent: PurchasePaymentIntentModel | null;
  onPayClick: () => void;
  color?: ICheckOutCombinedColors;
  onPaymentMethodIdUpdate: (paymentMethodId: string) => void;
  merchantAccount: MerchantAccountWithPaySettingsModel;
  seller: SellerModel;
  isPaymentInProgress?: boolean;
  action?: string;
  showSurchargeCheckbox?: boolean;
  includeSurcharge?: boolean;
  surchargePercentage?: number;
  isDonation?: boolean;
  accountId?: string;
  isPurchaseLink?: boolean;
  allowedPaymentMethods: string[];
};

let unmountPaymentElement: any;
const PurchasePayment = forwardRef<NMIPaymentMethodFormHandle, IProps>(
  (props, ref) => {
    const {
      allowedPaymentMethods,
      items,
      payDetails,
      paymentSectionData,
      updatePaymentSectionData,
      countriesWithState,
      addOns,
      updateItems,
      updateAddOns,
      paymentIntent,
      onPayClick,
      color,
      onPaymentMethodIdUpdate,
      merchantAccount,
      action,
      isPaymentInProgress,
      seller,
      showSurchargeCheckbox = false,
      includeSurcharge,
      surchargePercentage,
      isDonation,
      accountId,
      isPurchaseLink = false,
    } = props;
    React.useEffect(() => {
      if (window.Forward && merchantAccount.isFiservMerchantAccount()) {
        renderPaymentForm();
      } else {
        console.error("Forward library is not loaded.");
      }

      return () => {
        unmountPaymentElement && unmountPaymentElement();
      };
    }, []);

    async function getPaymentElement() {
      return (
        typeof window &&
        window.Forward.createPaymentElement({
          apiKey: process.env.REACT_APP_FISERV_API_KEY,
          clientSecret: paymentIntent?.clientSecret,
        })
      );
    }

    async function renderPaymentForm() {
      const paymentElement = await getPaymentElement();

      ///https://docs.getfwd.com/software-development-kits/payment-element
      unmountPaymentElement = paymentElement.mount("#payment-form", {
        onChange: (event: any) => {
          if (!event.ready) {
            console.log("Credit card input is incomplete");
            onPaymentMethodIdUpdate("");
            // Disable submit/pay button  and show error message
          }
        },
        onReady: (tokenize: any) => {
          //const totkenDetails = tokenize();
          // Enable submit/pay button and call `tokenize` on click
          //({billingDetails: {address: {postalCode: '12345'}}});

          tokenize();
          document
            .getElementById("pay-button")!
            .addEventListener("click", () => {
              const email =
                paymentSectionData.formData[CheckOutPayFields.EmailAddress];
              const fullName =
                paymentSectionData.formData["firstName"] +
                " " +
                paymentSectionData.formData["lastName"];
              tokenize({
                billingDetails: {
                  address: {
                    // postalCode: '12345',
                    email: email,
                    name: fullName,
                    //phone:
                  },
                },
              });
            });
          onPaymentMethodIdUpdate("");
        },
        onSuccess: async ({ token }: any) => {
          onPaymentMethodIdUpdate(token);

          // try {
          //     const response = await fetch('/api/process-payment', {
          //         method: 'POST',
          //         headers: {
          //             'Content-Type': 'application/json',
          //         },
          //         body: JSON.stringify({ paymentMethodId: token }),
          //     });

          //     if (response.ok) {
          //         console.log('Payment processed successfully');
          //     } else {
          //         console.error('Payment processing failed:', await response.text());
          //     }
          // } catch (error) {
          //     console.error('Error processing payment:', error);
          // }
        },
        onError: (error: any) => {
          console.log(error);
          onPaymentMethodIdUpdate("");
        },
        onCancel: () => {
          console.log("User closed Apple Pay or Google Pay pop-up window");
        },
      });
    }

    const renderFeedbackSection = () => {
      return (
        <FlexContainer className="feedback-section">
          <img src={apiResponse.feedbackData.url} alt="feedback" />
          <FlexContainer fxLayout="column" spacing={20}>
            <CustomLabel>{apiResponse.feedbackData.description}</CustomLabel>
            <CustomLabel>{apiResponse.feedbackData.header}</CustomLabel>
          </FlexContainer>
        </FlexContainer>
      );
    };

    const renderGuaranteeSection = () => {
      return (
        <FlexContainer className="guarantee-section">
          <img
            src={badgeIcon}
            alt="feedback"
            style={{ borderRadius: "50%", height: "96px" }}
          />
          <FlexContainer fxLayout="column" spacing={8}>
            <CustomLabel>Money Back Guarantee</CustomLabel>
            <CustomLabel>
              If you are not completely satisfied, return for a full refund
              within <span className="semi-bold text-nowrap">30 days.</span>
            </CustomLabel>
          </FlexContainer>
        </FlexContainer>
      );
    };

    const handleUpdates = (
      field: keyof PurchasePaymentModel,
      value:
        | string
        | boolean
        | { [key: string]: string }
        | CountryWithStateModel
        | AddressModel
        | PromotionModel,
    ) => {
      const clonedData = new PurchasePaymentModel({ ...paymentSectionData });
      switch (field) {
        case "formData":
          clonedData.formData = {
            ...clonedData.formData,
            ...(value as { [key: string]: string }),
          };
          break;
        case "acceptSellerTermsAndConditions":
          clonedData.acceptSellerTermsAndConditions = value as boolean;
          break;
        case "acceptReturnPolicy":
          clonedData.acceptReturnPolicy = value as boolean;
          break;
        case "acceptTermsAndConditions":
          clonedData.acceptTermsAndConditions = value as boolean;
          break;
        case "savePaymentMethod":
          clonedData.savePaymentMethod = value as boolean;
          break;
        case "updatesOffers":
          clonedData.updatesOffers = value as boolean;
          break;
        case "isPromotionClicked":
          clonedData.isPromotionClicked = value as boolean;
          break;
        case "promotionCodeValue":
          clonedData.promotionCodeValue = value as string;
          clonedData.applyDisabled = false;
          clonedData.promoText = "";
          break;
        case "applyDisabled":
          clonedData.applyDisabled = value as boolean;
          break;
        case "promoText":
          clonedData.promoText = value as string;
          clonedData.applyDisabled = true;
          break;
        case "countryDetails":
          clonedData.countryDetails = value as CountryWithStateModel;
          break;
        case "paymentMethodId":
          clonedData.paymentMethodId = value as string;
          break;
        case "taxId":
          clonedData.taxId = value as string;
          break;
        case "billingAddress":
          clonedData.billingAddress = value as AddressModel;
          break;
        case "shippingAddress":
          clonedData.shippingAddress = value as AddressModel;
          break;
        case "shippingSameAsBilling":
          clonedData.shippingSameAsBilling = value as boolean;
          if (value) {
            clonedData.shippingAddress = clonedData.billingAddress;
          }
          break;
        case "surchargeChecked":
          clonedData.surchargeChecked = value as boolean;
          break;
        case "promotionRelatedData":
          clonedData.promotionRelatedData = value as PromotionModel;
          break;
        default:
          break;
      }
      updatePaymentSectionData(clonedData);
    };

    const rednerPaymentMethodIcon = (method: string) => {
      switch (PaymentMethodTypes[method]) {
        case PaymentMethodTypes.CreditDebitCard:
          return (
            <>
              <img src={VisaIcon} alt="" />
              <img src={MasterCardIcon} alt="" />
              <img src={DiscoverIcon} alt="" />
              <img src={AmexIcon} alt="" />
            </>
          );
        case PaymentMethodTypes.Bank:
          return <img src={BankIcon} alt="" />;
        case PaymentMethodTypes.CheckWire:
          return AmexIcon;
        case PaymentMethodTypes.GooglePay:
          return <img src={GPayIcon} alt="" />;
        case PaymentMethodTypes.ApplePay:
          return <img src={ApplePayIcon} alt="" />;
        default:
          return null;
      }
    };
    return (
      <CheckoutItemsWrapper>
        {/* <MainOuterCard> */}
        <MultiplePriceWrapper
          className="payment-price-wrapper"
          // bgColor={color?.innerBackground?.rgba}
        >
          <FlexContainer className="divided-component wrapped-columns wrapped-max-width">
            <div
              className={
                paymentIntent && paymentIntent?.hasMultiplePaymentMethods()
                  ? "multiple-payment-method-form"
                  : "default-payment-method-form"
              }
            >
              <PurchasePaymentItemSection
                items={items}
                addOns={addOns}
                updateItems={(items: CustomerFacingCheckOutItemModel[]) =>
                  updateItems(items)
                }
                updateAddOns={(addOns: PurchaseCheckOutAddOnDetails[]) =>
                  updateAddOns(addOns)
                }
                merchantAccount={merchantAccount}
                includeSurcharge={includeSurcharge || false}
                surchargePercentage={surchargePercentage}
                paymentSectionData={paymentSectionData}
              />
              <PurchasePromotionSection
                paymentSectionData={paymentSectionData}
                handleDataChange={(
                  field: keyof PurchasePaymentModel,
                  value: string,
                ) => handleUpdates(field, value)}
                checkoutPaymentPageModel={payDetails}
                promoCodes={payDetails.promocodes}
                promoCodeSelectionType={payDetails.promoCodeSelectionType}
                accountId={accountId || ""}
                items={items}
                addOns={addOns}
                updatePaymentSectionData={updatePaymentSectionData}
                isPurchaseLink={isPurchaseLink}
              />
              <PurchasePaymentSummarySection
                items={items}
                addOns={addOns}
                merchantAccount={merchantAccount}
                includeSurcharge={includeSurcharge}
                surchargePercentage={surchargePercentage}
                paymentSectionData={paymentSectionData}
                promoCodeSelectionType={payDetails.promoCodeSelectionType}
                className={
                  payDetails.isMoneyBack || payDetails.isTestimonial
                    ? "summary-with-feedback"
                    : "summary-without-feedback"
                }
              />
              {(payDetails.isMoneyBack || payDetails.isTestimonial) && (
                <span className="feedback-at-left">
                  {payDetails.isMoneyBack && renderGuaranteeSection()}
                  {payDetails.isTestimonial && renderFeedbackSection()}
                </span>
              )}
            </div>
            <div>
              <FlexContainer fxLayout="column">
                {paymentIntent ? (
                  <div
                    className={`payment-form ${paymentIntent?.hasMultiplePaymentMethods() ? "fiserv-with-btn" : "fiserv-without-btn"}`}
                    id="payment-form"
                  ></div>
                ) : (
                  <NMIPaymentMethodForm
                    ref={ref}
                    onPaymentSuccess={(token) => onPaymentMethodIdUpdate(token)}
                    onPaymentError={() => onPaymentMethodIdUpdate("")}
                    merchantAccount={merchantAccount.getMerchantModel()}
                    contact={null}
                  />
                )}
                <PurchasePaymentFormInputs
                  merchantAccount={merchantAccount}
                  onPayClick={() => onPayClick()}
                  payDetails={payDetails}
                  paymentSectionData={paymentSectionData}
                  handleDataChange={(
                    field: keyof PurchasePaymentModel,
                    value: any,
                  ) => handleUpdates(field, value)}
                  countriesWithState={countriesWithState}
                  color={color}
                  addons={addOns}
                  items={items}
                  action={action}
                  seller={seller}
                  includeSurcharge={includeSurcharge}
                  surchargePercentage={surchargePercentage}
                  isDonation={isDonation}
                  isPaymentInProgress={isPaymentInProgress}
                  promoCodeSelectionType={payDetails.promoCodeSelectionType}
                  showSurchargeCheckbox={showSurchargeCheckbox}
                  hasMultiplePaymentMethods={paymentIntent?.hasMultiplePaymentMethods()}
                />
                <FlexContainer
                  spacing={6}
                  justifyContent="center"
                  alignItems="center"
                  className="card-icons"
                >
                  {allowedPaymentMethods.map((method) =>
                    rednerPaymentMethodIcon(method),
                  )}
                </FlexContainer>
                <span className="feedback-at-bottom">
                  <CustomLabel className="card-descriptions">
                    You allow{" "}
                    <span className="semi-bold">{merchantAccount.name}</span> to
                    charge your credit card now and future payments in
                    accordance with our terms and conditions. You can cancel
                    future recurring payments based on cancellation terms.
                    Changing your method of payment in the future may still
                    result in an amount due charged for any active subscriptions
                    unless cancelled.{" "}
                    <span className="text-royal-blue clickable">
                      View Terms.
                    </span>
                  </CustomLabel>
                </span>
              </FlexContainer>
            </div>
          </FlexContainer>
        </MultiplePriceWrapper>
        {/* </MainOuterCard> */}
      </CheckoutItemsWrapper>
    );
  },
);

export default observer(PurchasePayment);
